import bgLocale from "@fullcalendar/core/locales/bg";
import { useEffect, useState } from "react";
import { Title } from "react-admin";
import { Box, Chip, Dialog, DialogContent, Typography } from "@mui/material";
import ModalTitle, { withModalSx } from "server/components/Modals/ModalTitle";
import { studentRequests } from "api/studentApi";
import CalendarGlobalStyles from "./CalendarGlobalStyles";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { FromWeekToWeekChoices, NationalityType, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import { format } from "date-fns";
import SubstituteProfessorModal from "../components/SubstituteProfessorModal";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { getUserData } from "server/security/utils";
import { useNotifyError } from "utils/useNotifyError";
import dataProvider from "server/dataProvider";

const getMondayDate = date => {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return format(d, "yyyy-MM-dd");
};

const CalendarView = ({ filters }) => {
  const userData = getUserData();
  const notifyError = useNotifyError();
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const today = new Date();
  const [fromDate, setFromDate] = useState(getMondayDate(today));

  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getWeeklyTabbedScheduleCalendar({ ...filters, fromDate }),
          null,
          "GET",
        );
        if (data) {
          const calendarEvents = convertToCalendarEvents(data, fromDate);
          setEvents(calendarEvents);
        }
      } catch (e) {
        notifyError(e);
        console.error("fetchCalendarData error:", e);
      }
    };
    fetchCalendarData();
  }, [filters, fromDate, notifyError]);

  const handleDatesSet = info => {
    const startDate = info.start;
    const newFromDate = format(startDate, "yyyy-MM-dd");
    if (newFromDate !== fromDate) {
      setFromDate(newFromDate);
    }
  };

  const convertToCalendarEvents = (scheduleData, fromDate) => {
    const events = [];

    const monday = new Date(fromDate);
    const day = monday.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    monday.setDate(monday.getDate() + diffToMonday);

    scheduleData.forEach(item => {
      const eventDate = new Date(monday);
      eventDate.setDate(monday.getDate() + (item.weekDay - 1));

      const dateString = eventDate.toISOString().split("T")[0];

      const event = {
        id: `${item.id}`,
        start: `${dateString}T${item.startTime}`,
        end: `${dateString}T${item.endTime}`,
        extendedProps: item,
      };

      events.push(event);
    });

    return events;
  };

  const renderEventContent = eventInfo => {
    const props = eventInfo.event.extendedProps;
    const myId = userData.employeeId;

    let iHaveSubstitute = false;
    let amISubstitute = false;

    const substituteInfo = props.professors?.map((p, idx) => {
      if (!p.substituteEmployeeId) return null;
      const subId = p.substituteEmployeeId;
      const profId = p.employeeId;

      if (profId === myId && subId !== myId) iHaveSubstitute = true;
      if (subId === myId) amISubstitute = true;

      if (!amISubstitute && !iHaveSubstitute) return null;

      return (
        <span
          key={idx}
          className="discipline-value"
        >
          {iHaveSubstitute && "Имате избран заместник за това занятие!"}
          {amISubstitute && "Вие сте заместник за това занятие"}
        </span>
      );
    });

    return (
      <Box sx={{ p: 1, bgcolor: iHaveSubstitute ? "#bfc0c0" : "" }}>
        {substituteInfo}
        <div className="discipline-label">
          Дисциплина: <span className="discipline-value">{props.disciplineName}</span>
        </div>
        <div className="discipline-label">
          Тип занятие: <span className="discipline-value">{props.classTypeName}</span>
        </div>
        {props.curriculumModuleName && (
          <div className="discipline-label">
            Модул: <span className="discipline-value">{props.curriculumModuleName}</span>
          </div>
        )}
      </Box>
    );
  };

  const handleEventClick = eventInfo => {
    const props = eventInfo.event.extendedProps || {};
    const payload = {
      ...props,
      start: eventInfo.event.start ?? null,
      end: eventInfo.event.end ?? null,
    };
    setSelectedEvent(payload);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  return (
    <>
      <Title title={"Седмично разписание"} />
      <Box sx={{ px: 2, height: "800px", mb: 4 }}>
        <CalendarGlobalStyles />
        <FullCalendar
          plugins={[timeGridPlugin]}
          headerToolbar={{
            left: "prev today next",
            center: "title",
            right: "",
          }}
          initialDate={fromDate}
          datesSet={handleDatesSet}
          dayHeaderFormat={{
            weekday: "long",
            month: undefined,
            day: undefined,
          }}
          slotMinTime="07:00:00"
          slotMaxTime="21:00:00"
          locale={bgLocale}
          firstDay={1}
          allDaySlot={false}
          height="100%"
          events={events}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
        />
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={withModalSx()}
        >
          <ModalTitle
            title={
              filters.tab === 2
                ? selectedEvent?.professors
                    ?.map(p => {
                      return `${p.names} (${p.startWeekNumber}–${p.endWeekNumber} седмица)`;
                    })
                    .join(", ") || "-"
                : selectedEvent?.roomName || "Зала: Все още няма избрана зала"
            }
            color="warning"
            // Old look: red title on the light warning bar.
            legacySx={{ bgcolor: "warning.light", color: "error.main" }}
          />
          <DialogContent
            dividers
            sx={{ p: 4, bgcolor: theme => theme.palette.warning.light }}
          >
            <>
              {selectedEvent?.professors?.map((p, idx) => {
                if (!p.substituteEmployeeId) return null;
                const subId = p.substituteEmployeeId;
                const myId = userData.employeeId;
                const profId = p.employeeId;
                // СЛУЧАЙ А: Аз съм заместващият (Логнат съм като човекът, който ще води лекцията)
                const amISubstitute = subId === myId;
                // СЛУЧАЙ Б: Аз съм титулярът, но имам заместник (Логнат съм, но някой друг ще води вместо мен)
                const iHaveSubstitute = profId === myId && subId !== myId;
                if (!amISubstitute && !iHaveSubstitute) return null;
                return (
                  <Typography
                    key={idx}
                    sx={{ color: "error.primary", mb: 1, fontWeight: "bold" }}
                  >
                    {amISubstitute && "(Вие сте заместник за това занятие!)"}
                    {iHaveSubstitute && "(Има избран заместник за това занятие!)"}
                  </Typography>
                );
              })}
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                <Typography className="title">Специалности:</Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selectedEvent?.specialitiesNames?.map((spec, index) => (
                    <Chip
                      key={index}
                      label={spec}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
              <Typography className="title">
                Дисциплина: <strong className="value">{selectedEvent?.disciplineName}</strong>
              </Typography>
              <Typography className="title">
                Тип занятие: <strong className="value">{selectedEvent?.classTypeName}</strong>
              </Typography>
              {selectedEvent?.curriculumModuleName && (
                <Typography className="title">
                  Модул: <strong className="value">{selectedEvent?.curriculumModuleName}</strong>
                </Typography>
              )}
              {selectedEvent?.nationality && (
                <Typography className="title">
                  Националност:{" "}
                  <strong className="value">
                    {NationalityType.find(n => n.id === selectedEvent?.nationality)?.name}
                  </strong>
                </Typography>
              )}
              <Typography className="title">
                Курс: <strong className="value">{selectedEvent?.courseText}</strong>
              </Typography>
              <Typography className="title">
                Начален час: <strong className="value">{selectedEvent?.startTime}</strong>
              </Typography>
              <Typography className="title">
                Краен час:<strong className="value"> {selectedEvent?.endTime}</strong>
              </Typography>
              <Typography className="title">
                Език на преподаване:<strong className="value"> {selectedEvent?.instructionLanguageName}</strong>
              </Typography>
              <Typography className="title">
                Учебни групи:
                <strong className="value">
                  {" "}
                  {selectedEvent?.studyGroups.length > 0 ? selectedEvent?.studyGroups.join(", ") : "Всички"}
                </strong>
              </Typography>
              <Typography className="title">
                {filters.tab !== 2 ? "Преподаватели: " : "Зала: "}
                <strong className="value">
                  {filters.tab !== 2
                    ? `${
                        selectedEvent?.professors
                          ?.map(p => `${p.names} (${p.startWeekNumber}–${p.endWeekNumber} седмица)`)
                          .join(", ") || "-"
                      }`
                    : selectedEvent?.roomName || "Зала: Няма информация за зала"}
                </strong>
              </Typography>
              <Typography className="title">
                Тип седмица:
                <strong className="value"> {WeekTypeChoices.find(t => t.id === selectedEvent?.weekType)?.name}</strong>
              </Typography>
              <Typography className="title">
                Ден:
                <strong className="value"> {WeekDaysChoices.find(d => d.id === selectedEvent?.weekDay)?.name}</strong>
              </Typography>
              <Typography className="title">
                От седмица:
                <strong className="value">
                  {" "}
                  {FromWeekToWeekChoices.find(w => w.id === selectedEvent?.fromWeek)?.name}
                </strong>
              </Typography>
              <Typography
                className="title"
                mb={2}
              >
                До седмица:
                <strong className="value">
                  {" "}
                  {FromWeekToWeekChoices.find(w => w.id === selectedEvent?.toWeek)?.name}
                </strong>
              </Typography>
              {filters.tab === 2 ? (
                <SecuredComponent permission={permissions.canRequestScheduleProfessorSubstitute}>
                  <SubstituteProfessorModal
                    filters={filters}
                    record={selectedEvent}
                  />
                </SecuredComponent>
              ) : null}
            </>
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default CalendarView;
